import { forwardRef, useId } from 'react';

/**
 * Multi-line text input — same label/hint/error contract as TextField
 * (spec §7.1, §12), backed by `.etch-textarea`.
 *
 * @param {object} props
 * @param {string} [props.id]
 * @param {import('react').ReactNode} props.label
 * @param {boolean} [props.hideLabel]
 * @param {import('react').ReactNode} [props.hint]
 * @param {import('react').ReactNode} [props.error]
 * @param {boolean} [props.required]
 * @param {string} [props.className] - applied to the outer `.etch-field`.
 * @param {string} [props.textareaClassName] - applied to the `.etch-textarea`.
 */
export const TextArea = forwardRef(function TextArea(
  { id, label, hideLabel = false, hint, error, required, className = '', textareaClassName = '', ...rest },
  ref
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={`etch-field ${className}`.trim()}>
      <label htmlFor={inputId} className={`etch-field__label${hideLabel ? ' etch-visually-hidden' : ''}`}>
        {label}
        {required && (
          <span className="etch-field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <textarea
        ref={ref}
        id={inputId}
        className={`etch-textarea ${textareaClassName}`.trim()}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        {...rest}
      />
      {error ? (
        <span id={errorId} className="etch-field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span id={hintId} className="etch-field__hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
});

export default TextArea;
